import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import {
    FaCheckCircle, FaTimes,
    FaMap, FaCalendarAlt, FaUndo,
    FaQuestionCircle, FaBullhorn, FaFilePdf, FaDownload, FaUserNinja,
    FaCogs, FaUsers, FaKeyboard, FaChevronDown
} from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { AppContext } from '../../context/AppContext';
import PDFTASheetPage from './PDFTASheetPage';

// --- HELPER FOTO ---
const getPhotoUrl = (imageFileName) => {
    if (!imageFileName) return null;
    const baseUrl = process.env.REACT_APP_BACKEND || '';
    return `${baseUrl}/api/uploads/proxy?file=uploads/athletes/${imageFileName}&cb=${new Date().getTime()}`;
};

// --- HELPER LAPANGAN ABJAD ---
const getArenaLetter = (arenaNum) => String.fromCharCode(64 + parseInt(arenaNum || 1));

// --- HELPER DATA FESTIVAL ---
const normalizeText = (value) => String(value || '').toLowerCase().trim();

const isKyorugiFestivalAthlete = (athlete) => {
    return (
        normalizeText(athlete?.competition_type) === 'kyorugi' &&
        normalizeText(athlete?.type) === 'festival'
    );
};

const isEmptyByeMatch = (match) => {
    return !!match && !!match.is_bye && !match.athlete_blue_id && !match.athlete_red_id && !match.winner_id;
};

const getTeamName = (athlete) => {
    return athlete?.team?.name || athlete?.Team?.name || '-';
};

const getAthleteName = (athlete) => {
    return athlete?.name || 'TBD';
};

const getMatchNumberText = (match) => match?.match_num || match?.match_no || '-';

const buildFeedersByNextId = (matches = []) => matches.reduce((acc, match) => {
    if (isEmptyByeMatch(match)) return acc;
    if (!match?.next_match_id) return acc;

    const key = String(match.next_match_id);
    if (!acc[key]) acc[key] = [];
    acc[key].push(match);
    acc[key].sort((a, b) => (parseInt(a.match_no || 0) - parseInt(b.match_no || 0)) || (parseInt(a.id || 0) - parseInt(b.id || 0)));

    return acc;
}, {});

const getSlotDisplay = (match, corner, feedersByNextId = {}) => {
    const isBlue = corner === 'blue';
    const athlete = isBlue ? match?.BlueAthlete : match?.RedAthlete;
    const athleteId = isBlue ? match?.athlete_blue_id : match?.athlete_red_id;

    if (athlete) {
        return {
            athlete,
            athleteId,
            name: getAthleteName(athlete),
            team: getTeamName(athlete),
            belt: getAthleteBelt(athlete),
            pending: false
        };
    }

    if (match?.is_bye && !isBlue) {
        return {
            athlete: null,
            athleteId: null,
            name: 'BYE',
            team: 'OTOMATIS LOLOS',
            belt: '-',
            pending: false
        };
    }

    const feeders = feedersByNextId[String(match?.id)] || [];
    const feeder = feeders[isBlue ? 0 : 1];

    if (feeder) {
        return {
            athlete: null,
            athleteId: null,
            name: `Pemenang ${getMatchNumberText(feeder)}`,
            team: 'Menunggu hasil',
            belt: '-',
            pending: true
        };
    }

    return {
        athlete: null,
        athleteId: null,
        name: 'TBD',
        team: 'Menunggu lawan',
        belt: '-',
        pending: true
    };
};

const getAthleteWeight = (athlete) => {
    return athlete?.declared_weight || athlete?.weight || athlete?.actual_weight || '-';
};

const getAthleteBelt = (athlete) => {
    return athlete?.belt || '-';
};

const getFestivalPhysicalText = (athlete) => {
    if (!athlete) return '-';
    return `Sabuk: ${getAthleteBelt(athlete)} | U: ${athlete.age || '-'} | B: ${getAthleteWeight(athlete)} | T: ${athlete.height || '-'}`;
};

// ==========================================
// KOMPONEN KUSTOM: SEARCHABLE SELECT
// ==========================================
const SearchableSelect = ({ options, value, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);

    const selectedOption = options.find(opt => String(opt.value) === String(value));

    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
        } else {
            setSearchTerm(selectedOption ? selectedOption.label : '');
        }
    }, [isOpen, selectedOption]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt =>
        String(opt.label || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="position-relative w-100" ref={wrapperRef} style={{ zIndex: isOpen ? 1050 : 1 }}>
            <div
                className={`form-control bg-light border-0 py-2 px-3 rounded-3 d-flex justify-content-between align-items-center ${isOpen ? 'ring-primary' : ''}`}
                onClick={() => !isOpen && setIsOpen(true)}
                style={{ cursor: 'text' }}
            >
                {isOpen ? (
                    <input
                        type="text"
                        className="border-0 bg-transparent w-100 fw-bold text-dark"
                        style={{ outline: 'none', boxShadow: 'none', padding: 0 }}
                        placeholder="Ketik nama / klub..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        autoFocus
                    />
                ) : (
                    <div
                        className={`w-100 text-truncate fw-bold ${selectedOption && selectedOption.value !== '' ? 'text-dark' : 'text-muted'}`}
                        style={{ cursor: 'pointer' }}
                    >
                        {selectedOption ? selectedOption.label : placeholder}
                    </div>
                )}

                <FaChevronDown
                    className="text-muted ms-2 flex-shrink-0"
                    style={{
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        transform: isOpen ? 'rotate(180deg)' : 'none'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(!isOpen);
                    }}
                />
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown custom-scrollbar animate-fade-in">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(opt => (
                            <div
                                key={opt.value}
                                className={`searchable-option ${String(value) === String(opt.value) ? 'active' : ''}`}
                                onClick={() => {
                                    onChange(opt.value);
                                    setIsOpen(false);
                                }}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="px-3 py-3 text-center text-muted small fw-bold">
                            Tidak ada atlet ditemukan
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ==========================================
// STYLING PDF
// ==========================================
const pdfStyles = StyleSheet.create({
    page: { padding: 30, backgroundColor: '#ffffff', fontFamily: 'Helvetica' },
    header: { textAlign: 'center', marginBottom: 15, borderBottom: '2pt solid #000', paddingBottom: 10 },
    title: { fontSize: 16, fontWeight: 'bold', textTransform: 'uppercase' },
    subtitle: { fontSize: 10, fontWeight: 'bold', marginTop: 4 },
    table: { width: '100%', borderStyle: 'solid', borderWidth: 1, borderRightWidth: 0, borderBottomWidth: 0, marginTop: 10 },
    tableRow: { flexDirection: 'row' },
    tableHeader: { backgroundColor: '#e2e8f0', fontWeight: 'bold' },
    tableCol1: { width: '15%', borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 5 },
    tableCol2: { width: '35%', borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 5 },
    tableCol3: { width: '50%', borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 5 },
    tableCell: { fontSize: 9 }
});

const AdminKyorugiFestival = () => {
    const currentType = 'Festival';

    const { masterData, settings } = useContext(AppContext);

    const [selectedAthlete, setSelectedAthlete] = useState(null);
    const [activeTab, setActiveTab] = useState('generate');
    const [allAthletes, setAllAthletes] = useState([]);
    const [loading, setLoading] = useState(false);

    const [allMatches, setAllMatches] = useState([]);
    const [viewFilter, setViewFilter] = useState({ day: 1, arena: 1 });
    const [schedFilter, setSchedFilter] = useState({ day: 1, arena: 1 });

    const [showDetails, setShowDetails] = useState(false);

    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        action: null
    });

    const [printModal, setPrintModal] = useState({
        show: false,
        type: '',
        eventName: settings?.event_name || 'KEJUARAAN TAEKWONDO'
    });

    const [editMatchModal, setEditMatchModal] = useState({
        show: false,
        match: null,
        matchNum: '',
        sequence: '',
        blueId: '',
        redId: '',
        isManualBlue: false,
        newBlueName: '',
        newBlueTeam: '',
        isManualRed: false,
        newRedName: '',
        newRedTeam: ''
    });

    const [prestasiOccupied, setPrestasiOccupied] = useState(new Set());
    const [globalArenaLoads, setGlobalArenaLoads] = useState({});

    const [festivalParams, setFestivalParams] = useState({
        day: 1,
        selectedArenas: [],
        tol_age: 1,
        tol_weight: 3,
        tol_height: 6,
        comp_2: 50,
        comp_3: 30,
        comp_4: 20,
        groupingMode: 'weight',
        bracketMode: 'normal'
    });

    useEffect(() => {
        if (settings?.event_name) {
            setPrintModal(prev => ({
                ...prev,
                eventName: prev.eventName === 'KEJUARAAN TAEKWONDO'
                    ? settings.event_name
                    : prev.eventName
            }));
        }
    }, [settings?.event_name]);

    const fetchDashboardData = async () => {
        setLoading(true);

        try {
            const resAthletes = await api.get('/athletes/public-list');
            const rawAthletes = resAthletes.data.result || resAthletes.data || [];
            const kyorugiFestivalAthletes = rawAthletes.filter(a => isKyorugiFestivalAthlete(a));

            setAllAthletes(kyorugiFestivalAthletes);

            const resAll = await api.get('/matches', {
                params: { competition_type: 'Kyorugi' }
            });

            const allData = resAll.data.bracket || resAll.data.result || resAll.data || [];

            const filteredMatches = allData.filter(m => {
                const isPoolMatch = m.weight_class && (
                    m.weight_class.includes('(Pool') ||
                    m.weight_class.includes('Pool ')
                );

                return normalizeText(m.competition_type) === 'kyorugi' && isPoolMatch && !isEmptyByeMatch(m);
            });

            setAllMatches(filteredMatches);

            const occupied = new Set();
            const totalLoads = {};

            allData.forEach(m => {
                const isPool = m.weight_class && (
                    m.weight_class.includes('(Pool') ||
                    m.weight_class.includes('Pool ')
                );

                if (!isPool) occupied.add(`${m.day}-${m.arena}`);

                if (!m.is_bye) {
                    const lKey = `${m.day}-${m.arena}`;
                    totalLoads[lKey] = (totalLoads[lKey] || 0) + 1;
                }
            });

            setPrestasiOccupied(occupied);
            setGlobalArenaLoads(totalLoads);
        } catch (error) {
            console.error("🔴 Error Fetch Dashboard:", error);
            toast.error("Gagal memuat data Kyorugi Festival.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line
    }, [currentType]);

    const unpooledFestivalAthletes = useMemo(() => {
        const pooledIds = new Set();

        allMatches.forEach(m => {
            if (m.athlete_blue_id) pooledIds.add(m.athlete_blue_id);
            if (m.athlete_red_id) pooledIds.add(m.athlete_red_id);
        });

        return allAthletes.filter(a => (
            isKyorugiFestivalAthlete(a) &&
            !pooledIds.has(a.id)
        ));
    }, [allAthletes, allMatches]);

    const editableAthleteOptions = useMemo(() => {
        if (!editMatchModal.show || !editMatchModal.match) return [];

        const match = editMatchModal.match;

        return allAthletes
            .filter(a => (
                isKyorugiFestivalAthlete(a) &&
                a.gender === match.gender
            ))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [allAthletes, editMatchModal.show, editMatchModal.match]);

    const mappedAthleteOptions = useMemo(() => {
        const options = [{ label: '-- Kosongkan Slot --', value: '' }];

        editableAthleteOptions.forEach(a => {
            options.push({
                label: `${a.name} (${getTeamName(a)}) • ${getAthleteBelt(a)} • BB ${getAthleteWeight(a)} • TB ${a.height || '-'}`,
                value: a.id
            });
        });

        return options;
    }, [editableAthleteOptions]);

    const availableClassesForDropdown = useMemo(() => {
        const targetAlloc = `H${viewFilter.day}-Lap ${getArenaLetter(viewFilter.arena)}`;
        const map = {};

        allMatches.forEach(m => {
            const alloc = `H${m.day}-Lap ${getArenaLetter(m.arena)}`;

            if (alloc === targetAlloc) {
                const key = `${m.competition_type}|${m.gender}|${m.division}|${m.weight_class}`;

                if (!map[key]) {
                    map[key] = {
                        competition_type: m.competition_type,
                        gender: m.gender,
                        division: m.division,
                        weight_class: m.weight_class,
                        athleteCount: 0,
                        minMatchNum: 999999
                    };
                }

                const currentSeq = m.sequence || parseInt(m.match_no) || 999999;
                if (currentSeq < map[key].minMatchNum) map[key].minMatchNum = currentSeq;
            }
        });

        return Object.values(map).sort((a, b) => a.minMatchNum - b.minMatchNum);
    }, [viewFilter.day, viewFilter.arena, allMatches]);

    useEffect(() => {
        if (activeTab !== 'bracket' || loading || allMatches.length === 0 || availableClassesForDropdown.length > 0) return;

        const firstAvailablePool = allMatches.find(m =>
            m.weight_class && (
                m.weight_class.includes('(Pool') ||
                m.weight_class.includes('Pool ')
            )
        ) || allMatches[0];

        if (firstAvailablePool && (firstAvailablePool.day !== viewFilter.day || firstAvailablePool.arena !== viewFilter.arena)) {
            setViewFilter(prev => ({
                ...prev,
                day: firstAvailablePool.day,
                arena: firstAvailablePool.arena
            }));
        }
    }, [activeTab, loading, allMatches, availableClassesForDropdown.length, viewFilter.day, viewFilter.arena]);

    useEffect(() => {
        if (activeTab === 'schedule' && !loading && allMatches.length > 0) {
            const currentHasMatches = allMatches.some(m =>
                m.day === schedFilter.day &&
                m.arena === schedFilter.arena &&
                !m.is_bye
            );

            if (!currentHasMatches) {
                const firstAvailableMatch = allMatches.find(m => !m.is_bye);
                if (firstAvailableMatch) {
                    setSchedFilter({
                        day: firstAvailableMatch.day,
                        arena: firstAvailableMatch.arena
                    });
                }
            }
        }
    }, [activeTab, loading, allMatches, schedFilter.day, schedFilter.arena]);

    const executeGenerateFestival = async () => {
        const totalComp =
            parseInt(festivalParams.comp_2 || 0) +
            parseInt(festivalParams.comp_3 || 0) +
            parseInt(festivalParams.comp_4 || 0);

        if (totalComp !== 100) {
            return toast.warning(`Total persentase komposisi harus 100%! (Saat ini: ${totalComp}%)`);
        }

        if (festivalParams.selectedArenas.length === 0) {
            return toast.warning("Pilih minimal 1 lapangan untuk distribusi partai Festival!");
        }

        if (unpooledFestivalAthletes.length === 0) {
            return toast.info("Semua atlet Kyorugi Festival sudah masuk ke dalam bagan.");
        }

        setConfirmModal({
            show: true,
            title: 'Generate Bagan Festival?',
            message: `Sistem akan membentuk pool untuk ${unpooledFestivalAthletes.length} atlet secara otomatis berdasarkan toleransi fisik, prioritas beda klub, dan mendistribusikannya ke ${festivalParams.selectedArenas.length} lapangan. Lanjutkan?`,
            action: async () => {
                setConfirmModal({ show: false, title: '', message: '', action: null });
                setLoading(true);

                try {
                    await api.post('/matches/generate-festival', {
                        ...festivalParams,
                        type: 'Festival'
                    });

                    toast.success("Bagan Festival berhasil di-generate secara otomatis!");
                    setActiveTab('bracket');
                    fetchDashboardData();
                } catch (error) {
                    toast.error("Gagal generate Festival: " + (error.response?.data?.msg || "Internal Error"));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleSetWinnerClick = (matchUuid, winnerId, colorText) => {
        if (!winnerId) {
            return toast.warning(`Sudut ${colorText} belum memiliki atlet.`);
        }

        setConfirmModal({
            show: true,
            title: 'Konfirmasi Kemenangan',
            message: `Tetapkan Sudut ${colorText} sebagai pemenang?`,
            action: async () => {
                setConfirmModal({ show: false, title: '', message: '', action: null });

                try {
                    await api.patch(`/matches/winner/${matchUuid}`, { winner_id: winnerId });
                    toast.success("Pemenang ditetapkan!");
                    fetchDashboardData();
                } catch (error) {
                    toast.error("Gagal menetapkan pemenang.");
                }
            }
        });
    };

    const handleResetWinnerClick = (matchUuid) => {
        setConfirmModal({
            show: true,
            title: 'Batalkan Hasil',
            message: "Membatalkan pemenang mereset partai selanjutnya. Lanjut?",
            action: async () => {
                setConfirmModal({ show: false, title: '', message: '', action: null });

                try {
                    await api.patch(`/matches/reset/${matchUuid}`);
                    toast.success("Hasil dibatalkan.");
                    fetchDashboardData();
                } catch (error) {
                    toast.error("Gagal mereset.");
                }
            }
        });
    };

    const openEditMatchModal = (match) => {
        setEditMatchModal({
            show: true,
            match,
            matchNum: match.match_num || match.match_no || '',
            sequence: match.sequence || '',
            blueId: match.athlete_blue_id || '',
            redId: match.athlete_red_id || '',
            isManualBlue: false,
            newBlueName: '',
            newBlueTeam: '',
            isManualRed: false,
            newRedName: '',
            newRedTeam: ''
        });
    };

    const handleEditMatchSubmit = async () => {
        const matchNum = String(editMatchModal.matchNum || '').trim();

        if (!matchNum) {
            return toast.warning("Nama/nomor partai harus diisi.");
        }

        if (editMatchModal.isManualBlue && (!editMatchModal.newBlueName.trim() || !editMatchModal.newBlueTeam.trim())) {
            return toast.warning("Nama atlet & klub Sudut Biru (Baru) harus diisi!");
        }

        if (editMatchModal.isManualRed && (!editMatchModal.newRedName.trim() || !editMatchModal.newRedTeam.trim())) {
            return toast.warning("Nama atlet & klub Sudut Merah (Baru) harus diisi!");
        }

        const hadParticipant = !!(editMatchModal.match?.athlete_blue_id || editMatchModal.match?.athlete_red_id);
        const hasBlue = editMatchModal.isManualBlue || editMatchModal.blueId;
        const hasRed = editMatchModal.isManualRed || editMatchModal.redId;

        if (hadParticipant && !hasBlue && !hasRed) {
            return toast.warning("Minimal satu slot atlet harus terisi.");
        }

        if (hasBlue && hasRed && matchNum.toUpperCase() === 'BYE') {
            return toast.warning("Partai aktif dengan dua atlet tidak boleh memakai nama BYE.");
        }

        setLoading(true);

        try {
            await api.patch(`/matches/${editMatchModal.match.uuid}/manual-edit`, {
                match_num: matchNum,
                sequence: editMatchModal.sequence,
                athlete_blue_id: editMatchModal.isManualBlue ? 'manual' : (editMatchModal.blueId || null),
                new_blue_name: editMatchModal.isManualBlue ? editMatchModal.newBlueName : null,
                new_blue_team: editMatchModal.isManualBlue ? editMatchModal.newBlueTeam : null,
                athlete_red_id: editMatchModal.isManualRed ? 'manual' : (editMatchModal.redId || null),
                new_red_name: editMatchModal.isManualRed ? editMatchModal.newRedName : null,
                new_red_team: editMatchModal.isManualRed ? editMatchModal.newRedTeam : null
            });

            toast.success("Partai berhasil diperbarui.");

            setEditMatchModal({
                show: false,
                match: null,
                matchNum: '',
                sequence: '',
                blueId: '',
                redId: '',
                isManualBlue: false,
                newBlueName: '',
                newBlueTeam: '',
                isManualRed: false,
                newRedName: '',
                newRedTeam: ''
            });

            await fetchDashboardData();
        } catch (error) {
            toast.error("Gagal mengubah partai: " + (error.response?.data?.msg || "Internal Error"));
        } finally {
            setLoading(false);
        }
    };

    const handleCallAthlete = (m) => {
        const matchNum = m.match_num || m.match_no;

        const blueName = getAthleteName(m.BlueAthlete);
        const blueTeam = getTeamName(m.BlueAthlete);
        const blueBelt = getAthleteBelt(m.BlueAthlete);

        const redName = getAthleteName(m.RedAthlete);
        const redTeam = getTeamName(m.RedAthlete);
        const redBelt = getAthleteBelt(m.RedAthlete);

        const lapAbjad = getArenaLetter(m.arena);
        const className = m.weight_class || 'Kyorugi Festival';

        toast.info(`🔊 Panggilan Suara: Partai #${matchNum} ke Lapangan ${lapAbjad}`);

        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();

            const textToSpeak = `Panggilan kepada peserta Kyorugi Festival, partai nomor, ${matchNum}, kelas ${className}. Sudut biru, ${blueName}, sabuk ${blueBelt}, dari, ${blueTeam}. Melawan sudut merah, ${redName}, sabuk ${redBelt}, dari, ${redTeam}. Silakan segera menuju ke lapangan, ${lapAbjad}. Terima kasih.`;

            const utterance = new SpeechSynthesisUtterance(textToSpeak);
            utterance.lang = 'id-ID';
            utterance.rate = 0.85;

            window.speechSynthesis.speak(utterance);
        } else {
            toast.warning("Browser Anda tidak mendukung fitur panggilan suara.");
        }
    };

    const getRoundName = (roundNo, totalRounds) => {
        const r = parseInt(roundNo);
        const t = parseInt(totalRounds);

        if (r === t) return "Final";
        if (r === t - 1) return "Semifinal";
        if (r === t - 2) return "Quarter Final";

        return `Qualification ${r}`;
    };

    const BracketGridRenderer = ({ grouped, keys, totalR }) => {
        const renderMatches = Object.values(grouped).flat();
        const feedersByNextId = buildFeedersByNextId(renderMatches);

        return (
            <div className="d-flex bracket-wrapper pb-2 pt-4">
                {keys.map((round, rIdx) => (
                    <div
                        key={round}
                        className="bracket-column d-flex flex-column justify-content-around position-relative"
                        style={{ minWidth: '280px', padding: '0 25px' }}
                    >
                    <div className="text-center position-absolute w-100" style={{ top: '-30px', left: 0 }}>
                        <span className="badge bg-secondary bg-opacity-25 text-dark rounded-pill px-3 py-1 border shadow-sm fw-bold">
                            {getRoundName(round, totalR).toUpperCase()}
                        </span>
                    </div>

                    {grouped[round].map((m) => {
                        const isBlueWin = m.winner_id && m.winner_id === m.athlete_blue_id;
                        const isRedWin = m.winner_id && m.winner_id === m.athlete_red_id;
                        const blueSlot = getSlotDisplay(m, 'blue', feedersByNextId);
                        const redSlot = getSlotDisplay(m, 'red', feedersByNextId);

                        return (
                            <div key={m.id} className="match-card-wrapper position-relative my-2">
                                {rIdx < keys.length - 1 && <div className="connector-line-right"></div>}
                                {rIdx > 0 && <div className="connector-line-left"></div>}

                                {m.is_bye ? (
                                    <div className="card border-0 match-card overflow-hidden bg-light position-relative" style={{ borderRadius: '12px', border: '1px dashed #ccc' }}>
                                        <button
                                            className="btn btn-sm btn-warning position-absolute top-0 end-0 m-1 border shadow-sm rounded-pill d-flex justify-content-center align-items-center gap-1 fw-bold px-2 py-1 hover-scale no-print"
                                            style={{ zIndex: 20 }}
                                            title="Edit Partai"
                                            onClick={() => openEditMatchModal(m)}
                                        >
                                            <FaCogs size={10} />
                                            <span className="match-edit-label">Edit</span>
                                        </button>

                                        <div className="p-2 pe-5 d-flex align-items-center opacity-75">
                                            <div
                                                className="rounded-circle me-2 border border-2 border-secondary bg-white d-flex justify-content-center align-items-center text-muted"
                                                style={{ width: '30px', height: '30px', flexShrink: 0 }}
                                            >
                                                <FaUserNinja size={10} />
                                            </div>

                                            <div className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                                                <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.65rem' }}>
                                                    {getAthleteName(m.BlueAthlete)}
                                                </div>
                                                <div className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.5rem' }}>
                                                    BYE (OTOMATIS LOLOS)
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="card border-0 shadow-sm match-card overflow-hidden" style={{ borderRadius: '12px', border: '1px solid #ccc' }}>
                                        <div className="bg-light px-3 py-1 d-flex justify-content-between align-items-center gap-2 border-bottom">
                                            <span className="fw-bold text-muted text-truncate" style={{ fontSize: '0.6rem', minWidth: 0 }}>
                                                {m.weight_class?.split('(')[1]?.replace(')', '') || m.weight_class}
                                            </span>

                                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                <span className="fw-black text-primary text-nowrap" style={{ fontSize: '0.75rem' }}>
                                                    Partai #{m.match_num || m.match_no}
                                                </span>

                                                <button
                                                    className="btn btn-sm btn-warning border rounded-pill d-flex align-items-center justify-content-center gap-1 fw-bold px-2 py-1 no-print flex-shrink-0"
                                                    title="Edit Partai"
                                                    onClick={() => openEditMatchModal(m)}
                                                >
                                                    <FaCogs size={10} />
                                                    <span className="match-edit-label">Edit</span>
                                                </button>
                                            </div>
                                        </div>

                                        <div
                                            className={`p-2 border-bottom d-flex align-items-center cursor-pointer hover-bg-light ${isBlueWin ? 'bg-primary bg-opacity-10' : 'bg-white'}`}
                                            onClick={() => blueSlot.athlete && setSelectedAthlete(blueSlot.athlete)}
                                        >
                                            {m.BlueAthlete?.image ? (
                                                <img
                                                    crossOrigin="anonymous"
                                                    src={getPhotoUrl(m.BlueAthlete.image)}
                                                    alt=""
                                                    className="rounded-circle me-2 border border-2 border-primary"
                                                    style={{ width: '35px', height: '35px', objectFit: 'cover', flexShrink: 0 }}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded-circle me-2 border border-2 border-primary bg-light d-flex justify-content-center align-items-center text-muted"
                                                    style={{ width: '35px', height: '35px', flexShrink: 0 }}
                                                >
                                                    <FaUserNinja />
                                                </div>
                                            )}

                                            <div className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                                                <div className={`fw-black text-truncate ${isBlueWin ? 'text-primary' : blueSlot.pending ? 'text-secondary' : 'text-dark'}`} style={{ fontSize: '0.75rem' }}>
                                                    {blueSlot.name}
                                                </div>
                                                <div className="text-muted fw-bold text-uppercase text-truncate" style={{ fontSize: '0.55rem' }}>
                                                    {blueSlot.team}
                                                </div>

                                                {showDetails && blueSlot.athlete && (
                                                    <div className="text-danger fw-bold mt-1" style={{ fontSize: '0.55rem' }}>
                                                        {getFestivalPhysicalText(blueSlot.athlete)}
                                                    </div>
                                                )}
                                            </div>

                                            {isBlueWin && <FaCheckCircle className="text-primary ms-1 flex-shrink-0" size={14} />}
                                        </div>

                                        <div
                                            className={`p-2 d-flex align-items-center cursor-pointer hover-bg-light ${isRedWin ? 'bg-danger bg-opacity-10' : 'bg-white'}`}
                                            onClick={() => redSlot.athlete && setSelectedAthlete(redSlot.athlete)}
                                        >
                                            {m.RedAthlete?.image ? (
                                                <img
                                                    crossOrigin="anonymous"
                                                    src={getPhotoUrl(m.RedAthlete.image)}
                                                    alt=""
                                                    className="rounded-circle me-2 border border-2 border-danger"
                                                    style={{ width: '35px', height: '35px', objectFit: 'cover', flexShrink: 0 }}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded-circle me-2 border border-2 border-danger bg-light d-flex justify-content-center align-items-center text-muted"
                                                    style={{ width: '35px', height: '35px', flexShrink: 0 }}
                                                >
                                                    <FaUserNinja />
                                                </div>
                                            )}

                                            <div className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                                                <div className={`fw-black text-truncate ${isRedWin ? 'text-danger' : redSlot.pending ? 'text-secondary' : 'text-dark'}`} style={{ fontSize: '0.75rem' }}>
                                                    {redSlot.name}
                                                </div>
                                                <div className="text-muted fw-bold text-uppercase text-truncate" style={{ fontSize: '0.55rem' }}>
                                                    {redSlot.team}
                                                </div>

                                                {showDetails && redSlot.athlete && (
                                                    <div className="text-danger fw-bold mt-1" style={{ fontSize: '0.55rem' }}>
                                                        {getFestivalPhysicalText(redSlot.athlete)}
                                                    </div>
                                                )}
                                            </div>

                                            {isRedWin && <FaCheckCircle className="text-danger ms-1 flex-shrink-0" size={14} />}
                                        </div>
                                    </div>
                                )}

                                {!m.is_bye && (
                                    <div className="text-center mt-1 no-print" data-html2canvas-ignore="true">
                                        {m.BlueAthlete && m.RedAthlete && !m.winner_id && (
                                            <div className="d-flex justify-content-center gap-1">
                                                <button
                                                    className="btn btn-sm btn-primary fw-bold px-2 py-1"
                                                    style={{ fontSize: '0.6rem' }}
                                                    onClick={() => handleSetWinnerClick(m.uuid, m.athlete_blue_id, 'Biru')}
                                                >
                                                    Biru
                                                </button>

                                                <button
                                                    className="btn btn-sm btn-danger fw-bold px-2 py-1"
                                                    style={{ fontSize: '0.6rem' }}
                                                    onClick={() => handleSetWinnerClick(m.uuid, m.athlete_red_id, 'Merah')}
                                                >
                                                    Merah
                                                </button>
                                            </div>
                                        )}

                                        {m.winner_id && (
                                            <button
                                                className="btn btn-sm btn-light border text-danger fw-bold py-1 px-2"
                                                style={{ fontSize: '0.6rem' }}
                                                onClick={() => handleResetWinnerClick(m.uuid)}
                                            >
                                                <FaUndo className="me-1" /> Batal
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    </div>
                ))}
            </div>
        );
    };

    const executeTASheetExport = async (match) => {
        const toastId = toast.loading("Membentuk TA Sheet PDF...");

        try {
            const eventName = settings?.event_name || 'KEJUARAAN TAEKWONDO';
            const MyPDFDocument = (
                <Document>
                    <PDFTASheetPage eventName={eventName} match={match} variant="Kyorugi Festival" />
                </Document>
            );

            const blob = await pdf(MyPDFDocument).toBlob();
            saveAs(blob, `TA_Sheet_Partai_${match.match_num || match.match_no}.pdf`);

            toast.dismiss(toastId);
            toast.success("TA Sheet berhasil diunduh!");
        } catch (error) {
            console.error("Gagal memproses TA Sheet PDF:", error);
            toast.dismiss(toastId);
            toast.error("Gagal memproses TA Sheet PDF.");
        }
    };

    const PDFFestivalCompactBracket = ({ groupInfo, matches }) => {
        const bGrouped = matches.reduce((acc, m) => {
            const round = parseInt(m.round_no);

            if (!acc[round]) acc[round] = [];
            acc[round].push(m);

            return acc;
        }, {});

        const bKeys = Object.keys(bGrouped).map(Number).sort((a, b) => a - b);
        const feedersByNextId = buildFeedersByNextId(matches);

        const ui = {
            paddingRow: '2 4',
            nameSize: 7.2,
            teamSize: 5.4,
            badgeSize: 4.5,
            matchNumSize: 6,
            boxRadius: 2,
            paddingRightCol: 15,
            paddingRightFinal: 25,
            connectorWidth: 10,
            boxHeight: 44,
            textLineHeight: 1.08
        };

        const renderMatchBox = (m) => {
            const isBlueWin = m.winner_id && m.winner_id === m.athlete_blue_id;
            const isRedWin = m.winner_id && m.winner_id === m.athlete_red_id;
            const blueSlot = getSlotDisplay(m, 'blue', feedersByNextId);
            const redSlot = getSlotDisplay(m, 'red', feedersByNextId);

            return (
                <View style={{ flexDirection: 'row', alignItems: 'stretch', border: '1pt solid #000', borderRadius: ui.boxRadius, backgroundColor: '#fff', opacity: m.is_bye ? 0.6 : 1, zIndex: 2, height: ui.boxHeight }}>
                    <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', padding: ui.paddingRow, borderBottom: '0.5pt solid #000', backgroundColor: '#eff6ff', flex: 1 }}>
                            {isBlueWin && (
                                <View style={{ backgroundColor: '#16a34a', borderRadius: ui.boxRadius, padding: '2 3', marginRight: 4, alignSelf: 'center' }}>
                                    <Text style={{ fontSize: ui.badgeSize, fontWeight: 'bold', color: '#ffffff' }}>WIN</Text>
                                </View>
                            )}

                            <View style={{ flex: 1, justifyContent: 'center' }}>
                                <Text style={{ fontSize: ui.nameSize, fontWeight: 'bold', lineHeight: ui.textLineHeight, marginBottom: 0.5 }}>
                                    {blueSlot.name}
                                </Text>
                                <Text style={{ fontSize: ui.teamSize, lineHeight: ui.textLineHeight, color: blueSlot.pending ? '#94a3b8' : '#64748b' }}>
                                    {blueSlot.team}{blueSlot.pending ? '' : ` | ${blueSlot.belt}`}
                                </Text>
                            </View>
                        </View>

                        <View style={{ flexDirection: 'row', padding: ui.paddingRow, backgroundColor: '#fff', flex: 1 }}>
                            {isRedWin && (
                                <View style={{ backgroundColor: '#16a34a', borderRadius: ui.boxRadius, padding: '2 3', marginRight: 4, alignSelf: 'center' }}>
                                    <Text style={{ fontSize: ui.badgeSize, fontWeight: 'bold', color: '#ffffff' }}>WIN</Text>
                                </View>
                            )}

                            <View style={{ flex: 1, justifyContent: 'center' }}>
                                <Text style={{ fontSize: ui.nameSize, fontWeight: 'bold', lineHeight: ui.textLineHeight, marginBottom: 0.5 }}>
                                    {redSlot.name}
                                </Text>
                                <Text style={{ fontSize: ui.teamSize, lineHeight: ui.textLineHeight, color: redSlot.pending ? '#94a3b8' : '#64748b' }}>
                                    {redSlot.team}{redSlot.pending ? '' : ` | ${redSlot.belt}`}
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View style={{ width: 20, backgroundColor: '#e2e8f0', borderLeft: '1pt solid #000', justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={{ fontSize: ui.matchNumSize, fontWeight: 'bold' }}>
                            {m.is_bye ? 'BYE' : (m.match_num || m.match_no)}
                        </Text>
                    </View>
                </View>
            );
        };

        return (
            <View wrap={false} style={{ marginBottom: 15, width: '100%' }}>
                <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'flex-start' }}>
                    {bKeys.map((round, rIdx) => {
                        const isFinalRound = rIdx === bKeys.length - 1;
                        const matchesInRound = bGrouped[round];

                        const pairs = [];
                        for (let i = 0; i < matchesInRound.length; i += 2) {
                            pairs.push([matchesInRound[i], matchesInRound[i + 1]]);
                        }

                        return (
                            <View key={round} style={{ flex: 1, maxWidth: 220, flexDirection: 'column', justifyContent: 'space-around' }}>
                                {pairs.map((pair, pIdx) => (
                                    <View key={pIdx} style={{ position: 'relative', flexDirection: 'column', justifyContent: 'center', marginVertical: 3 }}>
                                        {pair[1] ? (
                                            <>
                                                <View style={{ paddingRight: isFinalRound ? ui.paddingRightFinal : ui.paddingRightCol, marginBottom: 3 }}>
                                                    {renderMatchBox(pair[0])}
                                                </View>

                                                <View style={{ paddingRight: isFinalRound ? ui.paddingRightFinal : ui.paddingRightCol, marginTop: 3 }}>
                                                    {renderMatchBox(pair[1])}
                                                </View>
                                            </>
                                        ) : (
                                            <View style={{ paddingRight: isFinalRound ? ui.paddingRightFinal : ui.paddingRightCol }}>
                                                {renderMatchBox(pair[0])}
                                            </View>
                                        )}

                                        {!isFinalRound && pair[1] && (
                                            <View style={{ position: 'absolute', right: ui.connectorWidth, top: '25%', bottom: '25%', width: ui.connectorWidth, borderTop: '1pt solid #000', borderBottom: '1pt solid #000', borderRight: '1pt solid #000', zIndex: 1 }} />
                                        )}

                                        {!isFinalRound && (
                                            <View style={{ position: 'absolute', right: 0, top: '50%', width: pair[1] ? ui.connectorWidth : (ui.connectorWidth * 2), borderBottom: '1pt solid #000', zIndex: 1 }} />
                                        )}
                                    </View>
                                ))}
                            </View>
                        );
                    })}
                </View>
            </View>
        );
    };

    const PDFSchedulePage = ({ matches, eventName, day, arena }) => (
        <Page size="A4" orientation="portrait" style={pdfStyles.page}>
            <View style={pdfStyles.header}>
                <Text style={pdfStyles.title}>{eventName}</Text>
                <Text style={pdfStyles.subtitle}>
                    JADWAL PEMANGGILAN PARTAI FESTIVAL (HARI {day} - LAPANGAN {getArenaLetter(arena)})
                </Text>
            </View>

            <View style={pdfStyles.table}>
                <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                    <View style={pdfStyles.tableCol1}>
                        <Text style={pdfStyles.tableCell}>Partai</Text>
                    </View>
                    <View style={pdfStyles.tableCol2}>
                        <Text style={pdfStyles.tableCell}>Kelas (Pool)</Text>
                    </View>
                    <View style={pdfStyles.tableCol3}>
                        <Text style={pdfStyles.tableCell}>Pertandingan (Merah vs Biru)</Text>
                    </View>
                </View>

                {matches.map((m, i) => (
                    <View style={pdfStyles.tableRow} key={i}>
                        <View style={pdfStyles.tableCol1}>
                            <Text style={[pdfStyles.tableCell, { fontWeight: 'bold' }]}>
                                #{m.match_num || m.match_no}
                            </Text>
                        </View>

                        <View style={pdfStyles.tableCol2}>
                            <Text style={pdfStyles.tableCell}>{m.weight_class}</Text>
                        </View>

                        <View style={pdfStyles.tableCol3}>
                            <Text style={pdfStyles.tableCell}>
                                {getAthleteName(m.RedAthlete)} ({getAthleteBelt(m.RedAthlete)}) vs {getAthleteName(m.BlueAthlete)} ({getAthleteBelt(m.BlueAthlete)})
                            </Text>
                        </View>
                    </View>
                ))}
            </View>
        </Page>
    );

    const executePDFExport = async () => {
        if (!printModal.eventName.trim()) {
            return toast.warning("Nama Event harus diisi!");
        }

        const toastId = toast.loading("Memproses PDF...");
        setPrintModal({ ...printModal, show: false });

        try {
            let MyPDFDocument;

            if (printModal.type === 'schedule') {
                const schedMatches = allMatches
                    .filter(m => m.day === schedFilter.day && m.arena === schedFilter.arena && !m.is_bye)
                    .sort((a, b) => a.sequence - b.sequence);

                MyPDFDocument = (
                    <Document>
                        <PDFSchedulePage
                            eventName={printModal.eventName}
                            day={schedFilter.day}
                            arena={schedFilter.arena}
                            matches={schedMatches}
                        />
                    </Document>
                );
            } else if (printModal.type === 'schedule-all-festival') {
                const schedGroups = {};

                allMatches.forEach(m => {
                    if (!m.is_bye) {
                        const key = `${m.day}-${m.arena}`;

                        if (!schedGroups[key]) {
                            schedGroups[key] = {
                                day: m.day,
                                arena: m.arena,
                                matches: []
                            };
                        }

                        schedGroups[key].matches.push(m);
                    }
                });

                const sortedSchedGroups = Object.values(schedGroups).sort((a, b) =>
                    a.day !== b.day ? a.day - b.day : a.arena - b.arena
                );

                sortedSchedGroups.forEach(g => g.matches.sort((a, b) => a.sequence - b.sequence));

                MyPDFDocument = (
                    <Document>
                        {sortedSchedGroups.map((g, idx) => (
                            <PDFSchedulePage
                                key={idx}
                                eventName={printModal.eventName}
                                day={g.day}
                                arena={g.arena}
                                matches={g.matches}
                            />
                        ))}
                    </Document>
                );
            } else {
                const pagesData = {};

                allMatches.forEach(m => {
                    const pageKey = `${m.day}-${m.arena}`;

                    if (!pagesData[pageKey]) {
                        pagesData[pageKey] = {
                            day: m.day,
                            arena: m.arena,
                            groups: {}
                        };
                    }

                    const groupKey = `${m.division}|${m.weight_class}|${m.gender}`;

                    if (!pagesData[pageKey].groups[groupKey]) {
                        pagesData[pageKey].groups[groupKey] = {
                            matches: [],
                            info: {
                                division: 'FESTIVAL',
                                weight_class: m.weight_class,
                                gender: m.gender,
                                day: m.day,
                                arena: m.arena
                            },
                            minSequence: 999999
                        };
                    }

                    pagesData[pageKey].groups[groupKey].matches.push(m);

                    const currentSeq = m.sequence || parseInt(m.match_no) || 999999;
                    if (currentSeq < pagesData[pageKey].groups[groupKey].minSequence) {
                        pagesData[pageKey].groups[groupKey].minSequence = currentSeq;
                    }
                });

                const sortedPages = Object.values(pagesData).sort((a, b) =>
                    a.day !== b.day ? a.day - b.day : a.arena - b.arena
                );

                MyPDFDocument = (
                    <Document>
                        {sortedPages.map((pageData, pIdx) => {
                            const sortedGroups = Object.values(pageData.groups).sort((a, b) => a.minSequence - b.minSequence);

                            return (
                                <Page key={`page-${pIdx}`} size="A4" orientation="portrait" style={{ padding: '30 25', backgroundColor: '#ffffff', fontFamily: 'Helvetica' }} wrap={true}>
                                    <View fixed style={{ textAlign: 'center', marginBottom: 15, borderBottom: '2pt solid #000', paddingBottom: 10 }}>
                                        <Text style={{ fontSize: 16, fontWeight: 'bold', textTransform: 'uppercase' }}>
                                            {printModal.eventName}
                                        </Text>
                                        <Text style={{ fontSize: 10, fontWeight: 'bold', marginTop: 4 }}>
                                            KESELURUHAN BAGAN FESTIVAL (HARI {pageData.day} - ARENA {getArenaLetter(pageData.arena)})
                                        </Text>
                                    </View>

                                    {sortedGroups.map((group, gIdx) => (
                                        <PDFFestivalCompactBracket
                                            key={`group-${gIdx}`}
                                            groupInfo={group.info}
                                            matches={group.matches}
                                        />
                                    ))}
                                </Page>
                            );
                        })}
                    </Document>
                );
            }

            const blob = await pdf(MyPDFDocument).toBlob();

            const fileName =
                printModal.type === 'schedule'
                    ? `Jadwal_H${schedFilter.day}_Lap${getArenaLetter(schedFilter.arena)}.pdf`
                    : printModal.type === 'schedule-all-festival'
                        ? `Seluruh_Jadwal_Festival.pdf`
                        : `Seluruh_Bagan_Festival.pdf`;

            saveAs(blob, fileName);

            toast.update(toastId, {
                render: "Dokumen diunduh!",
                type: "success",
                isLoading: false,
                autoClose: 3000
            });
        } catch (error) {
            console.error(error);
            toast.update(toastId, {
                render: "Gagal merakit PDF.",
                type: "error",
                isLoading: false,
                autoClose: 4000
            });
        }
    };

    const maxDays = masterData?.days || 1;
    const arenasPerDay = masterData?.arenasPerDay || [3];

    const scheduleMatchesToRender = allMatches
        .filter(m => m.day === schedFilter.day && m.arena === schedFilter.arena && !m.is_bye)
        .sort((a, b) => a.sequence - b.sequence);

    const renderScheduleParticipant = (match, corner) => {
        const isBlue = corner === 'blue';
        const athlete = isBlue ? match.BlueAthlete : match.RedAthlete;
        const athleteId = isBlue ? match.athlete_blue_id : match.athlete_red_id;
        const cornerText = isBlue ? 'Biru' : 'Merah';
        const borderClass = isBlue ? 'border-primary' : 'border-danger';
        const buttonClass = isBlue ? 'btn-primary' : 'btn-danger';

        const isWinner = match.winner_id && match.winner_id === athleteId;

        return (
            <div className={`festival-participant-row p-3 border-start border-4 ${borderClass} ${isBlue ? 'border-bottom' : ''}`}>
                <div className="festival-participant-info">
                    <div className="fw-bold text-dark text-truncate">
                        {getAthleteName(athlete)}
                    </div>
                    <div className="small text-muted text-truncate">
                        {getTeamName(athlete)} • Sabuk {getAthleteBelt(athlete)}
                    </div>
                    <div className="small text-muted">
                        BB {getAthleteWeight(athlete)} • TB {athlete?.height || '-'}
                    </div>
                </div>

                <div className="festival-action-area">
                    {isWinner ? (
                        <span className={`badge ${isBlue ? 'bg-primary' : 'bg-danger'} rounded-pill px-3 py-2`}>
                            Menang
                        </span>
                    ) : (
                        !match.winner_id && athleteId && (
                            <button
                                className={`btn btn-sm ${buttonClass} fw-bold rounded-pill festival-set-btn`}
                                onClick={() => handleSetWinnerClick(match.uuid, athleteId, cornerText)}
                            >
                                Set Menang
                            </button>
                        )
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid p-0 d-flex flex-column h-100 position-relative animate-fade-in" style={{ backgroundColor: '#f4f7fe' }}>
            <div className="mb-4 bg-white rounded-pill shadow-sm p-2 border d-flex gap-2 w-100 overflow-auto" style={{ maxWidth: 'fit-content' }}>
                <button
                    className={`btn rounded-pill px-4 py-2 fw-bold flex-fill text-nowrap transition-all ${activeTab === 'generate' ? 'btn-primary' : 'btn-white text-muted hover-bg-light'}`}
                    onClick={() => {
                        setActiveTab('generate');
                        fetchDashboardData();
                    }}
                >
                    <FaUsers className="me-2" /> Generator Festival
                </button>

                <button
                    className={`btn rounded-pill px-4 py-2 fw-bold flex-fill text-nowrap transition-all ${activeTab === 'bracket' ? 'btn-primary' : 'btn-white text-muted hover-bg-light'}`}
                    onClick={() => setActiveTab('bracket')}
                >
                    <FaMap className="me-2" /> Hasil Bagan Pool
                </button>

                <button
                    className={`btn rounded-pill px-4 py-2 fw-bold flex-fill text-nowrap transition-all ${activeTab === 'schedule' ? 'btn-primary' : 'btn-white text-muted hover-bg-light'}`}
                    onClick={() => {
                        setActiveTab('schedule');
                        fetchDashboardData();
                    }}
                >
                    <FaCalendarAlt className="me-2" /> Jadwal & Hasil
                </button>
            </div>

            {/* TAB 1: GENERATOR FESTIVAL */}
            {activeTab === 'generate' && (
                <div className="row g-3 pb-5">
                    <div className="col-lg-4 order-2 order-lg-1">
                        <div className="card border-0 shadow-sm rounded-4 h-100 bg-white">
                            <div className="card-header bg-transparent p-3 border-bottom d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="fw-bold text-dark m-0 small">Belum Ter-Pool</h6>
                                </div>
                                <span className="badge bg-danger rounded-pill px-3 py-2 shadow-sm">
                                    {unpooledFestivalAthletes.length} Atlet
                                </span>
                            </div>

                            <div className="card-body p-0 custom-scrollbar classes-list-container" style={{ maxHeight: '700px' }}>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary"></div>
                                    </div>
                                ) : unpooledFestivalAthletes.length === 0 ? (
                                    <div className="text-center py-5 text-muted small">
                                        <FaCheckCircle size={30} className="text-success mb-2 d-block mx-auto opacity-75" />
                                        Semua atlet sudah masuk ke bagan.
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {unpooledFestivalAthletes.map(a => (
                                            <div key={a.id} className="p-3 border-bottom d-flex align-items-center gap-3">
                                                <div
                                                    className="rounded-circle bg-primary bg-opacity-10 d-flex justify-content-center align-items-center text-primary flex-shrink-0"
                                                    style={{ width: '35px', height: '35px' }}
                                                >
                                                    <FaUserNinja />
                                                </div>

                                                <div className="flex-grow-1 overflow-hidden">
                                                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.8rem' }}>
                                                        {a.name}
                                                    </div>

                                                    <div className="text-muted text-uppercase text-truncate" style={{ fontSize: '0.65rem' }}>
                                                        {getTeamName(a)} | {a.gender} | {a.division}
                                                    </div>

                                                    <div className="mt-1 d-flex gap-2 flex-wrap">
                                                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: '0.6rem' }}>
                                                            Sabuk: {getAthleteBelt(a)}
                                                        </span>
                                                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.6rem' }}>
                                                            Umur: {a.age || '-'}
                                                        </span>
                                                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.6rem' }}>
                                                            BB: {getAthleteWeight(a)}
                                                        </span>
                                                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.6rem' }}>
                                                            TB: {a.height || '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-8 d-flex flex-column gap-3 order-1 order-lg-2">
                        <div className="card border-0 shadow-sm rounded-4 bg-white">
                            <div className="card-header bg-primary bg-opacity-10 p-3 border-bottom">
                                <h6 className="fw-bold text-primary m-0 small d-flex align-items-center">
                                    <FaCogs className="me-2" /> Parameter Pengelompokan Festival
                                </h6>
                            </div>

                            <div className="card-body p-4">
                                <h6 className="fw-bold text-dark small mb-3">Mode Generator</h6>

                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold text-muted mb-1">Mode Berat</label>
                                        <select
                                            className="form-select bg-light border-0 fw-bold"
                                            value={festivalParams.groupingMode}
                                            onChange={e => setFestivalParams({ ...festivalParams, groupingMode: e.target.value })}
                                        >
                                            <option value="weight">Selisih Berat</option>
                                            <option value="under">Kelas U Sama</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold text-muted mb-1">Mode Bagan</label>
                                        <select
                                            className="form-select bg-light border-0 fw-bold"
                                            value={festivalParams.bracketMode}
                                            onChange={e => setFestivalParams({ ...festivalParams, bracketMode: e.target.value })}
                                        >
                                            <option value="normal">Normal</option>
                                            <option value="bocil">Bocil 3-7 Tahun</option>
                                        </select>
                                    </div>
                                </div>

                                <h6 className="fw-bold text-dark small mb-3">Toleransi Kesamaan Fisik (Maksimal Selisih)</h6>

                                <div className="row g-3 mb-4">
                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted mb-1">Umur (Tahun)</label>
                                        <input
                                            type="number"
                                            className="form-control bg-light border-0 fw-bold"
                                            value={festivalParams.tol_age}
                                            onChange={e => setFestivalParams({ ...festivalParams, tol_age: e.target.value })}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted mb-1">Berat Badan (Kg)</label>
                                        <input
                                            type="number"
                                            className="form-control bg-light border-0 fw-bold"
                                            value={festivalParams.tol_weight}
                                            disabled={festivalParams.groupingMode === 'under'}
                                            onChange={e => setFestivalParams({ ...festivalParams, tol_weight: e.target.value })}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted mb-1">Tinggi Badan (Cm)</label>
                                        <input
                                            type="number"
                                            className="form-control bg-light border-0 fw-bold"
                                            value={festivalParams.tol_height}
                                            onChange={e => setFestivalParams({ ...festivalParams, tol_height: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <h6 className="fw-bold text-dark small mb-3">Target Komposisi Jumlah Atlet per Bagan (%)</h6>

                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted mb-1">Bagan 2 Orang</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control bg-light border-0 fw-bold"
                                                value={festivalParams.comp_2}
                                                onChange={e => setFestivalParams({ ...festivalParams, comp_2: e.target.value })}
                                            />
                                            <span className="input-group-text bg-light border-0 fw-bold text-muted">%</span>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted mb-1">Bagan 3 Orang</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control bg-light border-0 fw-bold"
                                                value={festivalParams.comp_3}
                                                onChange={e => setFestivalParams({ ...festivalParams, comp_3: e.target.value })}
                                            />
                                            <span className="input-group-text bg-light border-0 fw-bold text-muted">%</span>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted mb-1">Bagan 4 Orang</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control bg-light border-0 fw-bold"
                                                value={festivalParams.comp_4}
                                                onChange={e => setFestivalParams({ ...festivalParams, comp_4: e.target.value })}
                                            />
                                            <span className="input-group-text bg-light border-0 fw-bold text-muted">%</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 text-end">
                                    <small className={`fw-bold ${parseInt(festivalParams.comp_2 || 0) + parseInt(festivalParams.comp_3 || 0) + parseInt(festivalParams.comp_4 || 0) === 100 ? 'text-success' : 'text-danger'}`}>
                                        Total Persentase: {parseInt(festivalParams.comp_2 || 0) + parseInt(festivalParams.comp_3 || 0) + parseInt(festivalParams.comp_4 || 0)}%
                                    </small>
                                </div>
                            </div>
                        </div>

                        <div className="card border-0 shadow-sm rounded-4 bg-white">
                            <div className="card-header bg-transparent p-3 border-bottom d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold text-dark m-0 small d-flex align-items-center">
                                    <FaMap className="text-primary me-2" /> Distribusi Sisa Lapangan (Lintas Hari)
                                </h6>

                                <button
                                    className="btn btn-sm btn-outline-primary rounded-pill fw-bold shadow-sm hover-scale"
                                    style={{ fontSize: '0.65rem' }}
                                    onClick={() => {
                                        let autoArenas = [];

                                        for (let d = 1; d <= maxDays; d++) {
                                            for (let a = 1; a <= (arenasPerDay[d - 1] || 1); a++) {
                                                const load = globalArenaLoads[`${d}-${a}`] || 0;
                                                const isOcc = prestasiOccupied.has(`${d}-${a}`);

                                                if (load === 0 && !isOcc) autoArenas.push(`${d}-${a}`);
                                            }
                                        }

                                        if (autoArenas.length > 0) {
                                            setFestivalParams({ ...festivalParams, selectedArenas: autoArenas });
                                            toast.success(`Terpilih ${autoArenas.length} lapangan kosong.`);
                                        } else {
                                            toast.warning("Tidak ditemukan lapangan kosong.");
                                        }
                                    }}
                                >
                                    <FaCheckCircle className="me-1" /> Pilih Semua Sisa Lapangan
                                </button>
                            </div>

                            <div className="card-body p-3">
                                <label className="fw-bold text-dark small d-block mb-3">
                                    Pilih Lapangan untuk Festival (Otomatis bagi rata partai):
                                </label>

                                <div className="row g-2 mb-4">
                                    {[...Array(maxDays)].map((_, dIdx) => {
                                        const day = dIdx + 1;

                                        return [...Array(arenasPerDay[dIdx] || 1)].map((_, aIdx) => {
                                            const arena = aIdx + 1;
                                            const arenaKey = `${day}-${arena}`;
                                            const load = globalArenaLoads[arenaKey] || 0;
                                            const isOccupiedByPrestasi = prestasiOccupied.has(arenaKey);
                                            const isSelected = festivalParams.selectedArenas.includes(arenaKey);

                                            let loadColor = load > 40 ? 'text-danger' : load > 20 ? 'text-warning' : 'text-dark';

                                            return (
                                                <div className="col-4 col-md-3" key={`fest_ha_${day}_${arena}`}>
                                                    <button
                                                        type="button"
                                                        className={`btn w-100 p-2 border rounded-3 transition-all hover-scale ${isOccupiedByPrestasi ? 'bg-light border-secondary border-opacity-25 opacity-75' : isSelected ? 'btn-outline-primary border-2 bg-primary bg-opacity-10' : 'btn-white border-secondary border-opacity-25 hover-bg-light'}`}
                                                        disabled={isOccupiedByPrestasi}
                                                        onClick={() => {
                                                            let newArenas = [...festivalParams.selectedArenas];

                                                            if (isSelected) {
                                                                newArenas = newArenas.filter(a => a !== arenaKey);
                                                            } else {
                                                                newArenas.push(arenaKey);
                                                            }

                                                            setFestivalParams({ ...festivalParams, selectedArenas: newArenas });
                                                        }}
                                                    >
                                                        <div className="fw-bold text-muted mb-1" style={{ fontSize: '0.65rem' }}>
                                                            H{day}-Lap {getArenaLetter(arena)}
                                                        </div>

                                                        <h6 className={`fw-black m-0 ${isOccupiedByPrestasi ? 'text-danger' : isSelected ? 'text-primary' : loadColor}`}>
                                                            {load} <span className="d-none d-md-inline fw-normal text-muted" style={{ fontSize: '0.55rem' }}>Partai</span>
                                                        </h6>

                                                        {isOccupiedByPrestasi && (
                                                            <div className="text-danger fw-bold mt-1" style={{ fontSize: '0.5rem' }}>
                                                                PRESTASI
                                                            </div>
                                                        )}
                                                    </button>
                                                </div>
                                            );
                                        });
                                    })}
                                </div>

                                <button
                                    className="btn btn-warning w-100 py-3 rounded-pill fw-black shadow-sm text-dark fs-6 hover-scale d-flex justify-content-center align-items-center gap-2"
                                    onClick={executeGenerateFestival}
                                    disabled={unpooledFestivalAthletes.length === 0}
                                >
                                    <FaUsers /> GENERATE OTOMATIS BAGAN FESTIVAL
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: BAGAN */}
            {activeTab === 'bracket' && (
                <div className="card border-0 shadow-sm rounded-4 flex-grow-1 animate-fade-in bg-white mb-4" style={{ minHeight: '60vh' }}>
                    <div className="card-header bg-transparent p-4 border-bottom">
                        <div className="row g-3 justify-content-between align-items-center">
                            <div className="col-12 col-xl-2">
                                <h5 className="fw-bold m-0 text-dark">Hasil Bagan</h5>
                                <small className="text-muted">Kyorugi Festival</small>
                            </div>

                            <div className="col-12 col-xl-10 d-flex flex-wrap align-items-center justify-content-xl-end gap-2">
                                <div className="form-check form-switch me-2 d-flex align-items-center bg-light px-4 py-2 rounded-pill border">
                                    <input
                                        className="form-check-input mt-0"
                                        type="checkbox"
                                        id="toggleDetail"
                                        checked={showDetails}
                                        onChange={() => setShowDetails(!showDetails)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <label className="form-check-label ms-2 small fw-bold text-dark cursor-pointer" htmlFor="toggleDetail">
                                        QC Fisik
                                    </label>
                                </div>

                                <select
                                    className="form-select bg-primary bg-opacity-10 border-0 rounded-pill fw-bold px-3 py-2 w-auto"
                                    value={viewFilter.day}
                                    onChange={e => setViewFilter({ ...viewFilter, day: parseInt(e.target.value) })}
                                >
                                    {[...Array(maxDays)].map((_, i) => (
                                        <option key={i} value={i + 1}>Hari {i + 1}</option>
                                    ))}
                                </select>

                                <select
                                    className="form-select bg-primary bg-opacity-10 border-0 rounded-pill fw-bold px-3 py-2 w-auto"
                                    value={viewFilter.arena}
                                    onChange={e => setViewFilter({ ...viewFilter, arena: parseInt(e.target.value) })}
                                >
                                    {[...Array(arenasPerDay[viewFilter.day - 1] || 1)].map((_, i) => (
                                        <option key={i} value={i + 1}>Lap {getArenaLetter(i + 1)}</option>
                                    ))}
                                </select>

                                <div className="border-start mx-1" style={{ height: '20px' }}></div>

                                <button
                                    className="btn btn-success rounded-pill fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2 hover-scale"
                                    onClick={() => setPrintModal({ ...printModal, show: true, type: 'all-festival' })}
                                >
                                    <FaFilePdf /> Cetak Seluruh Festival
                                </button>

                                <button
                                    className="btn btn-light rounded-circle border shadow-sm p-2 hover-scale"
                                    onClick={fetchDashboardData}
                                >
                                    <FaUndo />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-0 bg-light overflow-auto bracket-scroll-container pb-5" style={{ minHeight: '400px' }}>
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary"></div>
                            </div>
                        ) : (
                            <div className="p-4 d-flex flex-column gap-4">
                                {availableClassesForDropdown.map((cls, idx) => {
                                    const matchesForThisPool = allMatches.filter(m =>
                                        m.day === viewFilter.day &&
                                        m.arena === viewFilter.arena &&
                                        m.weight_class?.startsWith(cls.weight_class) &&
                                        m.gender === cls.gender
                                    );

                                    const localGrouped = matchesForThisPool.reduce((acc, m) => {
                                        if (!acc[m.round_no]) acc[m.round_no] = [];
                                        acc[m.round_no].push(m);
                                        return acc;
                                    }, {});

                                    const localKeys = Object.keys(localGrouped).sort((a, b) => a - b);
                                    const localTotalR = localKeys.length > 0 ? parseInt(localKeys[localKeys.length - 1]) : 0;

                                    return (
                                        <div key={idx} className="bg-white p-2 rounded-4 shadow-sm border">
                                            <div className="overflow-auto">
                                                <BracketGridRenderer
                                                    grouped={localGrouped}
                                                    keys={localKeys}
                                                    totalR={localTotalR}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}

                                {availableClassesForDropdown.length === 0 && (
                                    <div className="text-center py-5 text-muted">
                                        <h5>Tidak ada jadwal Festival di Lapangan ini.</h5>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* TAB 3: SCHEDULE */}
            {activeTab === 'schedule' && (
                <div className="card border-0 shadow-sm rounded-4 flex-grow-1 animate-fade-in bg-white mb-4">
                    <div className="card-header bg-transparent p-4 border-bottom">
                        <div className="row justify-content-between align-items-center g-3">
                            <div className="col-12 col-md-auto">
                                <h5 className="fw-bold m-0 text-dark d-flex align-items-center gap-2">
                                    <FaCalendarAlt className="text-primary" /> Jadwal Festival
                                </h5>
                            </div>

                            <div className="col-12 col-md-auto">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <select
                                        className="form-select bg-light border-0 rounded-pill fw-bold px-3 py-2 w-auto"
                                        value={schedFilter.day}
                                        onChange={e => setSchedFilter({ ...schedFilter, day: parseInt(e.target.value) })}
                                    >
                                        {[...Array(maxDays)].map((_, i) => (
                                            <option key={i} value={i + 1}>Hari ke-{i + 1}</option>
                                        ))}
                                    </select>

                                    <select
                                        className="form-select bg-light border-0 rounded-pill fw-bold px-3 py-2 w-auto"
                                        value={schedFilter.arena}
                                        onChange={e => setSchedFilter({ ...schedFilter, arena: parseInt(e.target.value) })}
                                    >
                                        {[...Array(arenasPerDay[schedFilter.day - 1] || 1)].map((_, i) => (
                                            <option key={i} value={i + 1}>Lap {getArenaLetter(i + 1)}</option>
                                        ))}
                                    </select>

                                    <button
                                        className="btn btn-success rounded-pill fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2 hover-scale"
                                        onClick={() => setPrintModal({ ...printModal, show: true, type: 'schedule-all-festival' })}
                                    >
                                        <FaDownload /> Semua Jadwal Festival
                                    </button>

                                    <button
                                        className="btn btn-dark rounded-pill fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2 hover-scale"
                                        onClick={() => setPrintModal({ ...printModal, show: true, type: 'schedule' })}
                                    >
                                        <FaDownload /> Jadwal Lapangan Ini
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-4 bg-light rounded-bottom-4" style={{ backgroundColor: '#f8f9fa' }}>
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary"></div>
                            </div>
                        ) : scheduleMatchesToRender.length === 0 ? (
                            <div className="text-center py-5 text-muted">Kosong.</div>
                        ) : (
                            <div className="row g-4">
                                {scheduleMatchesToRender.map((m) => (
                                    <div className="col-md-6 col-lg-4" key={m.id}>
                                        <div className="card h-100 shadow-sm border-0 rounded-4 overflow-hidden match-card">
                                            <div className="bg-white p-3 border-bottom d-flex justify-content-between align-items-center gap-2">
                                                <span className="badge bg-light text-dark border px-3 py-2 fw-bold text-nowrap" style={{ fontSize: '0.8rem' }}>
                                                    #{m.match_num || m.match_no}
                                                </span>

                                                <div className="text-end flex-grow-1 text-truncate">
                                                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.7rem' }}>
                                                        {m.weight_class}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-body p-0 d-flex flex-column bg-white">
                                                {renderScheduleParticipant(m, 'blue')}
                                                {renderScheduleParticipant(m, 'red')}
                                            </div>

                                            <div className="card-footer bg-white border-top p-2 no-print d-flex gap-2" data-html2canvas-ignore="true">
                                                <button
                                                    className="btn btn-sm btn-light flex-fill text-muted small d-flex justify-content-center align-items-center gap-2 hover-bg-light fw-bold"
                                                    onClick={() => handleCallAthlete(m)}
                                                >
                                                    <FaBullhorn className="text-primary" /> Panggil
                                                </button>

                                                <button
                                                    className="btn btn-sm btn-light flex-fill text-muted small d-flex justify-content-center align-items-center gap-2 hover-bg-light fw-bold"
                                                    onClick={() => executeTASheetExport(m)}
                                                >
                                                    <FaFilePdf className="text-danger" /> TA Sheet
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* MODAL PRINT PDF INPUT EVENT NAME */}
            {printModal.show && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.6)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '90%', maxWidth: '450px', borderRadius: '20px' }}>
                        <div className="card-header bg-white p-4 border-bottom-0 d-flex justify-content-between align-items-center">
                            <h5 className="fw-black text-dark m-0">
                                <FaFilePdf className="text-danger me-2" /> Unduh PDF
                            </h5>
                            <button
                                onClick={() => setPrintModal({ ...printModal, show: false })}
                                className="btn btn-sm btn-light rounded-circle text-danger"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="card-body px-4 pb-4 pt-2">
                            <div className="mb-4">
                                <label className="form-label small fw-bold text-dark">Nama Event Kejuaraan</label>
                                <input
                                    type="text"
                                    className="form-control bg-light border-0 py-3 px-4 rounded-pill fw-bold"
                                    value={printModal.eventName}
                                    onChange={(e) => setPrintModal({ ...printModal, eventName: e.target.value })}
                                    placeholder="Contoh: KEJUARAAN BUPATI CUP"
                                />
                            </div>

                            <div className="d-flex gap-2 justify-content-end">
                                <button
                                    className="btn btn-light rounded-pill px-4 fw-bold"
                                    onClick={() => setPrintModal({ ...printModal, show: false })}
                                >
                                    Batal
                                </button>

                                <button
                                    className="btn btn-primary rounded-pill px-4 fw-bold"
                                    onClick={executePDFExport}
                                >
                                    Render & Simpan
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* MODAL CONFIRMATION */}
            {confirmModal.show && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.5)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '90%', maxWidth: '400px', borderRadius: '24px' }}>
                        <div className="card-body p-5 text-center">
                            <div className="mb-3">
                                <FaQuestionCircle size={55} className="text-warning opacity-75" />
                            </div>

                            <h5 className="fw-bold text-dark mb-2">{confirmModal.title}</h5>
                            <p className="text-muted small mb-4">{confirmModal.message}</p>

                            <div className="d-flex gap-2 justify-content-center">
                                <button
                                    className="btn btn-light border rounded-pill px-4 fw-bold"
                                    onClick={() => setConfirmModal({ show: false, title: '', message: '', action: null })}
                                >
                                    Batal
                                </button>

                                <button
                                    className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm"
                                    onClick={confirmModal.action}
                                >
                                    Ya, Lanjutkan
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* MODAL EDIT PARTAI */}
            {editMatchModal.show && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.6)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '92%', maxWidth: '560px', borderRadius: '20px' }}>
                        <div className="card-header bg-white p-4 border-bottom-0 d-flex justify-content-between align-items-center">
                            <h5 className="fw-black text-dark m-0 d-flex align-items-center gap-2">
                                <FaCogs className="text-primary" /> Edit Partai Festival
                            </h5>

                            <button
                                onClick={() => setEditMatchModal({
                                    show: false,
                                    match: null,
                                    matchNum: '',
                                    sequence: '',
                                    blueId: '',
                                    redId: '',
                                    isManualBlue: false,
                                    newBlueName: '',
                                    newBlueTeam: '',
                                    isManualRed: false,
                                    newRedName: '',
                                    newRedTeam: ''
                                })}
                                className="btn btn-sm btn-light rounded-circle text-danger"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="card-body px-4 pb-4 pt-2">
                            <div className="row g-3 mb-3">
                                <div className="col-md-7">
                                    <label className="form-label small fw-bold text-dark">Nama / Nomor Partai</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light border-0 py-2 px-3 rounded-3 fw-bold"
                                        value={editMatchModal.matchNum}
                                        onChange={e => setEditMatchModal({ ...editMatchModal, matchNum: e.target.value })}
                                    />
                                </div>

                                <div className="col-md-5">
                                    <label className="form-label small fw-bold text-dark">Urutan Jadwal (Seq)</label>
                                    <input
                                        type="number"
                                        className="form-control bg-light border-0 py-2 px-3 rounded-3 fw-bold"
                                        value={editMatchModal.sequence}
                                        onChange={e => setEditMatchModal({ ...editMatchModal, sequence: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="row g-3 mb-4" style={{ position: 'relative', zIndex: 1050 }}>
                                <div className="col-md-6 border-end" style={{ position: 'relative', zIndex: 1060 }}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label small fw-bold text-primary m-0">Sudut Biru</label>

                                        <div className="form-check form-switch m-0 d-flex align-items-center gap-1">
                                            <input
                                                className="form-check-input mt-0 cursor-pointer"
                                                type="checkbox"
                                                id="manualBlue"
                                                checked={editMatchModal.isManualBlue}
                                                onChange={() => setEditMatchModal({
                                                    ...editMatchModal,
                                                    isManualBlue: !editMatchModal.isManualBlue,
                                                    blueId: ''
                                                })}
                                            />
                                            <label className="form-check-label text-muted cursor-pointer" style={{ fontSize: '0.65rem' }} htmlFor="manualBlue">
                                                <FaKeyboard /> Ketik Baru
                                            </label>
                                        </div>
                                    </div>

                                    {editMatchModal.isManualBlue ? (
                                        <div className="p-2 bg-primary bg-opacity-10 rounded-3 border border-primary border-opacity-25 animate-fade-in">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm border-0 mb-2 fw-bold shadow-sm"
                                                placeholder="Ketik Nama Atlet..."
                                                value={editMatchModal.newBlueName}
                                                onChange={e => setEditMatchModal({ ...editMatchModal, newBlueName: e.target.value })}
                                            />

                                            <input
                                                type="text"
                                                className="form-control form-control-sm border-0 fw-bold text-uppercase shadow-sm"
                                                placeholder="Ketik Nama Klub..."
                                                value={editMatchModal.newBlueTeam}
                                                onChange={e => setEditMatchModal({ ...editMatchModal, newBlueTeam: e.target.value })}
                                            />
                                        </div>
                                    ) : (
                                        <SearchableSelect
                                            options={mappedAthleteOptions}
                                            value={editMatchModal.blueId}
                                            onChange={(val) => setEditMatchModal({ ...editMatchModal, blueId: val })}
                                            placeholder="-- Pilih Atlet Biru --"
                                        />
                                    )}
                                </div>

                                <div className="col-md-6" style={{ position: 'relative', zIndex: 1050 }}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label small fw-bold text-danger m-0">Sudut Merah</label>

                                        <div className="form-check form-switch m-0 d-flex align-items-center gap-1">
                                            <input
                                                className="form-check-input mt-0 cursor-pointer"
                                                type="checkbox"
                                                id="manualRed"
                                                checked={editMatchModal.isManualRed}
                                                onChange={() => setEditMatchModal({
                                                    ...editMatchModal,
                                                    isManualRed: !editMatchModal.isManualRed,
                                                    redId: ''
                                                })}
                                            />
                                            <label className="form-check-label text-muted cursor-pointer" style={{ fontSize: '0.65rem' }} htmlFor="manualRed">
                                                <FaKeyboard /> Ketik Baru
                                            </label>
                                        </div>
                                    </div>

                                    {editMatchModal.isManualRed ? (
                                        <div className="p-2 bg-danger bg-opacity-10 rounded-3 border border-danger border-opacity-25 animate-fade-in">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm border-0 mb-2 fw-bold shadow-sm"
                                                placeholder="Ketik Nama Atlet..."
                                                value={editMatchModal.newRedName}
                                                onChange={e => setEditMatchModal({ ...editMatchModal, newRedName: e.target.value })}
                                            />

                                            <input
                                                type="text"
                                                className="form-control form-control-sm border-0 fw-bold text-uppercase shadow-sm"
                                                placeholder="Ketik Nama Klub..."
                                                value={editMatchModal.newRedTeam}
                                                onChange={e => setEditMatchModal({ ...editMatchModal, newRedTeam: e.target.value })}
                                            />
                                        </div>
                                    ) : (
                                        <SearchableSelect
                                            options={mappedAthleteOptions}
                                            value={editMatchModal.redId}
                                            onChange={(val) => setEditMatchModal({ ...editMatchModal, redId: val })}
                                            placeholder="-- Pilih Atlet Merah --"
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="alert alert-warning border-0 rounded-3 small py-2 px-3 mb-4" style={{ position: 'relative', zIndex: 1 }}>
                                <strong>Penting:</strong> Jika "Ketik Baru" diaktifkan, sistem akan mendaftarkan nama dan klub tersebut menjadi atlet resmi di database agar bisa mencetak sertifikat.
                            </div>

                            <div className="d-flex gap-2 justify-content-end">
                                <button
                                    className="btn btn-light rounded-pill px-4 fw-bold"
                                    onClick={() => setEditMatchModal({
                                        show: false,
                                        match: null,
                                        matchNum: '',
                                        sequence: '',
                                        blueId: '',
                                        redId: '',
                                        isManualBlue: false,
                                        newBlueName: '',
                                        newBlueTeam: '',
                                        isManualRed: false,
                                        newRedName: '',
                                        newRedTeam: ''
                                    })}
                                >
                                    Batal
                                </button>

                                <button
                                    className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm"
                                    onClick={handleEditMatchSubmit}
                                    disabled={loading}
                                >
                                    Simpan Perubahan
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                .animate-pop-in { animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
                .transition-all { transition: all 0.2s ease-in-out; }
                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.05); }
                .hover-bg-light:hover { background-color: #f8f9fa !important; }
                .cursor-pointer { cursor: pointer; }

                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 10px; }

                .searchable-select-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    width: 100%;
                    max-height: 220px;
                    overflow-y: auto;
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 0.5rem;
                    z-index: 1050;
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
                    margin-top: 0.3rem;
                }

                .searchable-option { 
                    padding: 0.6rem 1rem; 
                    cursor: pointer; 
                    transition: background-color 0.1s;
                    font-size: 0.85rem;
                    color: #495057;
                }

                .searchable-option:hover { background-color: #f8f9fa; color: #000; }
                .searchable-option.active { background-color: rgba(13, 110, 253, 0.1); color: #0d6efd; font-weight: bold; }

                .ring-primary {
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
                    border: 1px solid #86b7fe !important;
                }

                .classes-list-container { max-height: 400px; overflow-y: auto; }

                @media (min-width: 992px) {
                    .classes-list-container { max-height: 750px; }
                }

                .bracket-scroll-container {
                    position: relative;
                    width: 100%;
                    cursor: grab;
                }

                .bracket-wrapper {
                    display: flex;
                    flex-direction: row;
                    align-items: stretch;
                    margin-top: 20px;
                    padding-bottom: 50px;
                }

                .connector-line-right {
                    position: absolute;
                    right: -25px;
                    top: 50%;
                    width: 25px;
                    height: 2px;
                    background-color: #cbd5e1;
                    z-index: 1;
                }

                .connector-line-left {
                    position: absolute;
                    left: -25px;
                    top: 50%;
                    width: 25px;
                    height: 2px;
                    background-color: #cbd5e1;
                    z-index: 1;
                }

                .match-card-wrapper {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    z-index: 2;
                    page-break-inside: avoid;
                }

                .match-card {
                    min-width: 240px;
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .match-edit-label {
                    font-size: 0.6rem;
                    line-height: 1;
                }

                .festival-participant-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    background: #ffffff;
                    min-height: 96px;
                }

                .festival-participant-info {
                    min-width: 0;
                    flex: 1 1 auto;
                    overflow: hidden;
                }

                .festival-action-area {
                    flex: 0 0 auto;
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    min-width: 96px;
                }

                .festival-set-btn {
                    white-space: nowrap;
                    min-width: 96px;
                    font-size: 0.75rem;
                    padding: 0.4rem 0.75rem;
                }

                @media (max-width: 420px) {
                    .festival-participant-row {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .festival-action-area {
                        width: 100%;
                        justify-content: flex-start;
                    }
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes popIn {
                    0% { transform: scale(0.9); opacity: 0; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>

            {/* MODAL DETAIL ATLET */}
            {selectedAthlete && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 2000, background: 'rgba(0,0,0,0.6)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '90%', maxWidth: '350px', borderRadius: '20px' }}>
                        <div className="card-body p-4 text-center">
                            <div
                                className="rounded-circle bg-primary bg-opacity-10 mx-auto mb-3 d-flex justify-content-center align-items-center overflow-hidden"
                                style={{ width: '60px', height: '60px' }}
                            >
                                {selectedAthlete.image ? (
                                    <img
                                        crossOrigin="anonymous"
                                        src={getPhotoUrl(selectedAthlete.image)}
                                        alt=""
                                        className="w-100 h-100"
                                        style={{ objectFit: 'cover' }}
                                    />
                                ) : (
                                    <FaUserNinja size={30} className="text-primary" />
                                )}
                            </div>

                            <h6 className="fw-black mb-1">{getAthleteName(selectedAthlete)}</h6>
                            <p className="text-muted small mb-3">{getTeamName(selectedAthlete)}</p>

                            <div className="mb-3">
                                <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2 rounded-pill">
                                    Sabuk: {getAthleteBelt(selectedAthlete)}
                                </span>
                            </div>

                            <div className="row g-2">
                                <div className="col-4">
                                    <div className="bg-light p-2 rounded-3">
                                        <small className="d-block text-muted">Umur</small>
                                        <strong className="text-dark">{selectedAthlete.age || '-'}</strong>
                                    </div>
                                </div>

                                <div className="col-4">
                                    <div className="bg-light p-2 rounded-3">
                                        <small className="d-block text-muted">Berat</small>
                                        <strong className="text-dark">{getAthleteWeight(selectedAthlete)}</strong>
                                    </div>
                                </div>

                                <div className="col-4">
                                    <div className="bg-light p-2 rounded-3">
                                        <small className="d-block text-muted">Tinggi</small>
                                        <strong className="text-dark">{selectedAthlete.height || '-'}</strong>
                                    </div>
                                </div>
                            </div>

                            <button
                                className="btn btn-primary w-100 rounded-pill mt-4 fw-bold"
                                onClick={() => setSelectedAthlete(null)}
                            >
                                Tutup
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default AdminKyorugiFestival;
